import {api} from "@/shared/api";

import {BasicDataTable, TypedColumnSettings} from "@/shared/ui/basicDataTable";
import {SelectableDataTable, SelectableDataTableConfig} from "@/shared/ui/selectableDataTable";
import {DataTableBulkSelector} from "@/shared/ui/dataTableBulkSelector";
import * as Constants from "@/shared/constants";
import {BulkSelectorContextMenu} from "@/shared/ui/bulkSelectorContextMenu";
import {
	formatRecordedEntryForValidationMessage,
	getStartEndTimeForMissingRecordingValidation
} from "@/shared/validations/recordingValidationUtil";
import {getWorkingTimeTypesForAddAbsence, getWorkingTimeTypesForAddWorkingTime} from "@/shared/securityHelper";
import {ReportingQueryParams} from "@/shared/queryParams";
import {statusElementsHolder} from "@/shared/recording/statusElementsHolder";
import {htmlEscape} from "@/shared/utils/string";
import {formatDate, formatDateDay, parseISODate, parseISOPlainDateTime, Temporal} from "@/shared/utils/datefunctions";
import {ValidationResult, WorkingTimeType} from "@/shared/timr";
import {
	RecordingValidationsMode,
	RecordingValidationStatus,
	ReportViewType,
	ValidationSeverity,
	WorkingTimeValidationCategory
} from "@/shared/timrTypes";

export interface RecordingValidationTableConfig extends SelectableDataTableConfig {
	isGroupLeaderForThemself: boolean;
	workingTimeTypes: WorkingTimeType[];
}

export class RecordingValidationTable extends SelectableDataTable<ValidationResult> {
	public static EVENT_ADD_WORKTIME = "addWorktime";
	public static EVENT_ADD_ABSENCE = "addAbsence";

	public static COLUMN_SIZE_USER = "200px";
	public static COLUMN_SIZE_DATE = "150px";

	private static COLUMN_NAME_USER = "user";
	private static COLUMN_INDEX_DATE = 2;

	private managerMode = (globals.isGroupLeader || globals.isTaskLeader) ? RecordingValidationsMode.Manager : RecordingValidationsMode.Personal;
	private addTimeContextMenu: BulkSelectorContextMenu;

	constructor(public config: RecordingValidationTableConfig) {
		super("/timr/reports/getRecordingValidationsForTable.json", config);

		this.addTimeContextMenu = this.createAddTimeContextMenu();

		this.config.element.on("click", ".setStatusButton", (e) => {
			const tr = $(e.target as HTMLElement).closest("tr");
			const recording = this.getRowData(tr);
			const triggerElement = e.currentTarget instanceof HTMLElement ? e.currentTarget : undefined;
			this.setStatus(recording.id, this.managerMode === RecordingValidationsMode.Manager ? RecordingValidationStatus.CONFIRMED : RecordingValidationStatus.ACKNOWLEDGED, triggerElement);
		});
		this.dataTable.on("processing.dt", (e, settings, processing) => {
			if (!processing) {
				this.addTimeContextMenu.registerOnButton(this.config.element.find(".addTimeDropdown"), true);
			}
		});
	}

	public setManagerMode(managerMode: RecordingValidationsMode) {
		this.managerMode = managerMode;
		this.toggleUserColumn(this.managerMode === RecordingValidationsMode.Manager);
	}

	protected createBulkSelector(): DataTableBulkSelector<ValidationResult> {
		return new DataTableBulkSelector({dataTable: this.dataTable});
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			paging: true,
			pageLength: Constants.REPORT_TABLES_PAGE_LENGTH,
			serverSide: true,
			dom: `rt<"datatable-footer"ip>`
		};
	}

	protected getColumnSettings(): TypedColumnSettings<ValidationResult>[] {
		return [
			{
				data: "id",
				orderable: false,
				render: BasicDataTable.renderSelectCheckbox,
				visible: true,
				width: BasicDataTable.COLUMN_SIZE_CHECKBOX
			},
			{data: "id", width: "44px", orderable: false, className: "context-menu", render: this.contextMenu.render},
			{data: "day", orderable: false, render: this.renderDateInclDay, width: RecordingValidationTable.COLUMN_SIZE_DATE},
			{
				data: "user.fullname",
				name: RecordingValidationTable.COLUMN_NAME_USER,
				orderable: false,
				visible: globals.isGroupLeader || globals.isTaskLeader,
				render: BasicDataTable.renderText,
				width: RecordingValidationTable.COLUMN_SIZE_USER
			},
			{data: "messageCode", orderable: false, render: this.renderWarningMessage},
			{
				data: "day",
				orderable: false,
				render: (date, _t, validationResult) => this.renderShowInTimelineButton(parseISODate(date), validationResult),
				className: "timeline-link-column"
			},
			{data: "status", orderable: false, render: this.renderStatusText},
			// Second messageCode column for the add time button -> check if the code signals a missing entry
			{
				data: "messageCode",
				orderable: false,
				render: (d, t, r, m) => this.renderAddTimeButton(d),
				className: "context-menu",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			},
			{
				data: "status",
				orderable: false,
				render: (d, t, r, m) => this.renderStatusButton(d, t, r, m),
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			},
			{
				data: "severity",
				orderable: false,
				render: this.renderSeverityIcon,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			}
		];
	}

	private renderDateInclDay(this: void, data: string, type: string, recording: ValidationResult, meta: DataTables.CellMetaSettings): string {
		const date = parseISODate(data);
		return date ? formatDateDay(date) + ", " + formatDate(date) : "";
	}

	/**
	 * Renders the warning message from the ValidationResultDTO which contains:
	 * 	- messageCode: key for the message source
	 * 	- messageArguments (optional): arguments for the message source
	 * 	- recordedEntry, recordedEntryRef (optional): relevant recordings to be opened directly via dialog
	 *
	 * e.g. "Daily rest period is too short (2022-07-31 to 2022-08-01) 06:00 -> 22:04 and 07:00 -> 12:00"
	 * @param data messageCode
	 * @param type
	 * @param row ValidationResultDTO
	 * @param meta
	 * @private
	 */
	private renderWarningMessage(this: void, data: string, type: string, row: ValidationResult, meta: DataTables.CellMetaSettings) {
		let message = htmlEscape(row.messageArguments ? messages.withArguments(data, ...row.messageArguments) : messages[data]);
		if (row.recordedEntry) {
			message += formatRecordedEntryForValidationMessage(row.recordedEntry, globals.showTimeZone);
		}
		if (row.recordedEntryRef) {
			message += ` ${messages["timeAccount.validationProblems.and"]} ` + formatRecordedEntryForValidationMessage(row.recordedEntryRef, globals.showTimeZone);
		}
		return message;
	}

	/**
	 * Renders the severity icon appropriate to the importance
	 * This icon is a warning triangle, which differs in color and shape depending on the severity
	 * @param data ValidationSeverity
	 * @param type
	 * @param row ValidationResultDTO
	 * @param meta
	 * @protected
	 */
	protected renderSeverityIcon(this: void, data: ValidationSeverity, type: string, recording: ValidationResult, meta: DataTables.CellMetaSettings) {
		return `<i class="fa fa-validation-severity-${data} position-left" title="${messages["recordingValidation.severity." + data]}"></i>`;
	}

	protected renderStatusButton(status: RecordingValidationStatus, type: string, recording: ValidationResult, meta: DataTables.CellMetaSettings) {
		return status === RecordingValidationStatus.OPEN || status === RecordingValidationStatus.ACKNOWLEDGED && this.managerMode === RecordingValidationsMode.Manager ?
			`<button type="button" class="btn btn-default btn-xs setStatusButton">${messages["recordingValidation.button." + this.managerMode]}</button>` :
			"";
	}

	protected renderAddTimeButton(data: string): string {
		return data === "validationResult.missingEntryOnWorkingDay" || data === "validationResult.missingEntryOnHalfDayHoliday" ?
			$("<ul>").addClass("list-icon")
				.append($("<li>").addClass("dropdown")
					.append($("<button>", {
						"data-toggle": "dropdown",
						"aria-expanded": "false"
					}).addClass("dropdown-toggle").addClass("btn btn-primary btn-xs addTimeDropdown")
						.append($("<i>").addClass("far fa-plus position-left"))
						.append($("<span>").text(messages["recordingValidations.add"])))
					.append(this.addTimeContextMenu.render())
				).prop("outerHTML") as string
			: "";
	}

	private renderShowInTimelineButton(date: Temporal.PlainDate | null, validation: ValidationResult) {
		const isWeeklyValidation = validation.category === WorkingTimeValidationCategory.WEEKLY_REST_PERIOD_INSIDE
			|| validation.category === WorkingTimeValidationCategory.WEEKLY_REST_PERIOD_TOO_SHORT
			|| validation.category === WorkingTimeValidationCategory.MAXIMUM_WORK_DURATION_WEEKLY;

		if (this.managerMode === RecordingValidationsMode.Manager) {
			const link = ReportingQueryParams.buildLink({
				userId: validation.user.id,
				date,
				viewType: isWeeklyValidation ? ReportViewType.WEEKLY : ReportViewType.DAILY
			}, "reports/redirectToReporting");

			return `<a href="${link}">${messages[isWeeklyValidation ? "recordingValidations.viewWeek" : "recordingValidations.viewDay"]}</a>`;
		} else {
			const link = ReportingQueryParams.buildLink({
				userId: validation.user.id,
				date,
				viewType: isWeeklyValidation ? ReportViewType.WEEKLY : ReportViewType.DAILY
			}, "reports/timeline.html");

			return `<a href="${link}">${messages["recordingValidations.viewTimeline"]}</a>`;
		}
	}

	protected renderStatusText(this: void, data: RecordingValidationStatus, type: string, recording: ValidationResult, meta: DataTables.CellMetaSettings) {
		return messages["recordingValidation.status." + data];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[RecordingValidationTable.COLUMN_INDEX_DATE, "desc"]];
	}

	protected createContextMenu() {
		return super.createContextMenu()
			.addMenuEntry({
				label: messages.withArguments("recordingValidation.context.setStatus", messages["recordingValidation.status.OPEN"]),
				active: validation => validation.allowedTargetStates.some(status => status === RecordingValidationStatus.OPEN),
				action: validation => this.setStatus(validation.id, RecordingValidationStatus.OPEN)
			})
			.addMenuEntry({
				label: messages.withArguments("recordingValidation.context.setStatus", messages["recordingValidation.status.ACKNOWLEDGED"]),
				active: validation => validation.allowedTargetStates.some(status => status === RecordingValidationStatus.ACKNOWLEDGED),
				action: validation => this.setStatus(validation.id, RecordingValidationStatus.ACKNOWLEDGED)
			})
			.addMenuEntry({
				label: messages.withArguments("recordingValidation.context.setStatus", messages["recordingValidation.status.CONFIRMED"]),
				active: validation => validation.allowedTargetStates.some(status => status === RecordingValidationStatus.CONFIRMED),
				action: validation => this.setStatus(validation.id, RecordingValidationStatus.CONFIRMED),
				show: () => this.managerMode === RecordingValidationsMode.Manager
			})
			.addMenuEntry({
				label: messages.withArguments("recordingValidation.context.setStatus", messages["recordingValidation.status.CLOSED"]),
				active: validation => validation.allowedTargetStates.some(status => status === RecordingValidationStatus.CLOSED),
				action: validation => this.setStatus(validation.id, RecordingValidationStatus.CLOSED),
				show: () => this.managerMode === RecordingValidationsMode.Manager
			});
	}

	protected createAddTimeContextMenu(): BulkSelectorContextMenu {
		return new BulkSelectorContextMenu()
			.addMenuEntry({
				label: messages["recordingValidations.context.addWorkingTime"],
				active: getWorkingTimeTypesForAddWorkingTime(this.config.workingTimeTypes).length > 0,
				action: (e, contextElement) => {
					const tr = contextElement.closest("tr");
					const validation = this.getRowData(tr);
					void getStartEndTimeForMissingRecordingValidation(validation.id, null, null).done(startEndTime => {
						const start = parseISOPlainDateTime(startEndTime.start);
						const end = parseISOPlainDateTime(startEndTime.end);
						this.config.element.trigger(RecordingValidationTable.EVENT_ADD_WORKTIME, [validation, start, end]);
					});
				}
			})
			.addMenuEntry({
				label: messages["recordingValidations.context.addAbsence"],
				active: getWorkingTimeTypesForAddAbsence(this.config.workingTimeTypes).length > 0,
				action: (e, contextElement) => {
					const tr = contextElement.closest("tr");
					const validation = this.getRowData(tr);
					void getStartEndTimeForMissingRecordingValidation(validation.id, null, null).done(startEndTime => {
						const start = parseISOPlainDateTime(startEndTime.start);
						const end = parseISOPlainDateTime(startEndTime.end);
						this.config.element.trigger(RecordingValidationTable.EVENT_ADD_ABSENCE, [validation, start, end]);
					});
				}
			});
	}

	private setStatus(validationId: number, status: RecordingValidationStatus, triggerElement?: HTMLElement) {
		// TIMR-11022: Prevent the browser from auto-scrolling to the next focusable element when the triggering button disappears during reload.
		triggerElement?.blur();

		api("reports/setRecordingValidationStatus.json")
			.post({id: validationId, status: status})
			.done(() => {
				statusElementsHolder.updateStatus();
				this.reload();
			});
	}

	/**
	 * Toggles user column based on the transmitted value
	 * @param show
	 * @private
	 */
	private toggleUserColumn(show: boolean) {
		this.dataTable.column(RecordingValidationTable.COLUMN_NAME_USER + ":name").visible(show);
	}
}
